import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { FinancialAssumptions } from '../../types/financialAssumptions';
import {
  baseMergedScenario,
  resolveCanonicalCore,
  type MergedScenario,
} from '../financialScenarios';
import { recompute } from '../projectFinance';
import { computeSiteComposite } from './compositeScorer';
import { bandForScore, pillarToDecisionScore } from './decisionBands';
import type { DecisionBandId } from './decisionBands';
import type { PillarId } from './pillarRegistry';

export interface ProjectedComposite {
  scenarioId: string;
  scenarioLabel: string;
  projectedComposite: number;
  projectedBand: DecisionBandId;
  projectedBandLabel: string;
  projectedBandColor: string;
  confidenceTag: 'V' | 'R' | 'C';
  deltaFromCanonical: number;
  pillarShifts: Record<PillarId, { from: number; to: number; delta: number }>;
  bandCrossing: boolean;
}

export class ScenarioProjectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScenarioProjectionError';
  }
}

function toEffectiveFinancialAssumptions(
  site: SiteEntity,
  mergedScenario: MergedScenario,
): FinancialAssumptions {
  const base = site.financialAssumptions;
  return {
    ...(base ?? {}),
    ...mergedScenario.effective,
    scenarios: base?.scenarios,
  } as FinancialAssumptions;
}

function patchReportsWithScenarioFinancials(
  reports: SiteRagReport[],
  effectiveFa: FinancialAssumptions,
  lcoe: number | null,
): SiteRagReport[] {
  return reports.map((report) => ({
    ...report,
    financialAssumptionsAtGen: effectiveFa,
    financialAssumptionsOverride: effectiveFa,
    lcoeDetailedAnalysis: {
      ...(report.lcoeDetailedAnalysis ?? {}),
      lcoe: lcoe ?? undefined,
    },
  }) as SiteRagReport);
}

/**
 * The row every projection is measured against.
 *
 * EXPLICIT canonical only (A9): when no scenario carries `isCanonical` the
 * baseline is the UNMODIFIED base assumptions, never `scenarios[0]` — which
 * used to make an arbitrary (often stress-case) scenario the reference the
 * whole panel, and the audit's scenario-robustness check, compared against.
 */
function canonicalMergedScenario(site: SiteEntity): MergedScenario {
  const fa = site.financialAssumptions;
  if (!fa) {
    throw new ScenarioProjectionError('Projection unavailable — missing financial assumptions');
  }
  const scenarios = fa.scenarios ?? [];
  if (scenarios.length === 0) {
    throw new ScenarioProjectionError('Projection unavailable — incomplete scenario inputs');
  }
  const { core, canonical } = resolveCanonicalCore(fa);
  if (!canonical) return baseMergedScenario(fa);
  return {
    scenario: canonical,
    effective: core,
    metrics: recompute(core),
  };
}

export function projectCompositeUnderScenario(
  site: SiteEntity,
  reports: SiteRagReport[],
  scenario: MergedScenario,
): ProjectedComposite {
  if (!site.financialAssumptions) {
    throw new ScenarioProjectionError('Projection unavailable — missing financial assumptions');
  }

  const customerId = site.companyId ?? 'unknown';
  const canonicalScenario = canonicalMergedScenario(site);

  const canonicalFa = toEffectiveFinancialAssumptions(site, canonicalScenario);
  const canonicalReports = patchReportsWithScenarioFinancials(
    reports,
    canonicalFa,
    canonicalScenario.metrics.lcoeNominalPerMwhUsd,
  );
  const canonicalComposite = computeSiteComposite({
    site,
    customerId,
    reports: canonicalReports,
  });

  const projectedFa = toEffectiveFinancialAssumptions(site, scenario);
  const projectedReports = patchReportsWithScenarioFinancials(
    reports,
    projectedFa,
    scenario.metrics.lcoeNominalPerMwhUsd,
  );
  const projectedComposite = computeSiteComposite({
    site,
    customerId,
    reports: projectedReports,
  });

  const pillarShifts = {} as Record<PillarId, { from: number; to: number; delta: number }>;
  for (const pillarId of Object.keys(canonicalComposite.pillars) as PillarId[]) {
    const canonicalPillar = canonicalComposite.pillars[pillarId];
    const projectedPillar = projectedComposite.pillars[pillarId];
    const from = pillarToDecisionScore(canonicalPillar?.score ?? 0);
    const to = pillarToDecisionScore(projectedPillar?.score ?? 0);
    pillarShifts[pillarId] = { from, to, delta: to - from };
  }

  const projectedDecisionScore = projectedComposite.decisionScore;
  const canonicalDecisionScore = canonicalComposite.decisionScore;
  const projectedBand = bandForScore(projectedDecisionScore);

  return {
    scenarioId: scenario.scenario.id,
    scenarioLabel: scenario.scenario.label,
    projectedComposite: projectedDecisionScore,
    projectedBand: projectedComposite.decisionBand,
    projectedBandLabel: projectedComposite.decisionBandLabel,
    projectedBandColor: projectedBand.color,
    confidenceTag: projectedComposite.confidenceTag,
    deltaFromCanonical: projectedDecisionScore - canonicalDecisionScore,
    pillarShifts,
    bandCrossing: projectedComposite.decisionBand !== canonicalComposite.decisionBand,
  };
}
